/**
 * Calendarios, en Configuración › Integraciones.
 *
 * Es la puerta, no otro sitio donde configurarlo: la sincronización vive en
 * Reservas (es por persona, junto a su horario) y aquí se enseña su estado y se
 * abre LA MISMA ventana. Está porque quien quiere «conectar Google Calendar» lo
 * busca en Integraciones, y si no lo encuentra ahí cree que no existe.
 *
 * Sin servicios con cita no hay nada que sincronizar: se dice y se lleva a
 * crear uno.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconBrandApple, IconBrandWindows, IconCalendarEvent, IconCalendarShare, IconCheck, IconListCheck, IconPlus } from '@tabler/icons-react'
import { GoogleCalendarLogo } from './GoogleCalendarMark'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { CalendarSyncDialog } from './CalendarSyncDialog'
import { getBookingSchedule, getCalendarSync, listOffers, type BookingResource, type CalendarSyncState } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'

/** El logo de un calendario por enlace, por el dominio del que viene. */
function feedLogo(host: string) {
  if (/google\./i.test(host)) return <GoogleCalendarLogo size={18} />
  if (/outlook|office|live\.com|microsoft/i.test(host)) return <span className="is-outlook"><IconBrandWindows size={16} /></span>
  if (/icloud|apple/i.test(host)) return <span className="is-apple"><IconBrandApple size={17} /></span>
  return <IconCalendarEvent size={16} />
}

export function CalendarsSettings({ projectId, token, onBack, onOpenBookings }: {
  projectId: string
  token: string
  onBack?: (() => void) | undefined
  /** A Reservas: donde se crean los servicios con cita. */
  onOpenBookings: () => void
}) {
  const { t } = useI18n()
  const [state, setState] = useState<CalendarSyncState | null>(null)
  const [resources, setResources] = useState<BookingResource[]>([])
  const [hasServices, setHasServices] = useState(false)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      const [sync, schedule, offers] = await Promise.all([
        getCalendarSync(projectId, token),
        getBookingSchedule(projectId, token),
        listOffers(projectId, token),
      ])
      setState(sync)
      setResources(schedule.resources)
      setHasServices(offers.some((offer) => offer.type === 'service' && offer.booking.durationMinutes))
      setFailed(false)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [projectId, token])

  useEffect(() => { void load() }, [load])

  const google = state?.googleAccounts ?? []
  const feeds = state?.feeds ?? []
  const connected = google.length > 0 || feeds.length > 0 || Boolean(state?.exportUrl)

  /* Una línea por cosa conectada, con lo que el dueño reconoce: su logo, y la
     cuenta de Google por su correo, los demás por el nombre que les puso. */
  const lines: { key: string; logo: React.ReactNode; text: string }[] = [
    ...google.map((account) => ({ key: `g-${account.id}`, logo: <GoogleCalendarLogo size={18} />, text: t('calset.google', { email: account.email }) })),
    ...feeds.map((feed) => ({ key: `f-${feed.id}`, logo: feedLogo(feed.host), text: t('calset.feed', { name: feed.name || feed.host }) })),
    ...(state?.exportUrl ? [{ key: 'export', logo: <IconCalendarShare size={16} />, text: t('calset.export') }] : []),
  ]

  return (
    <ProjectSettingsLayout title={t('section.calendars')} description={t('calset.description')} onBack={onBack}>
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon dash-settings-brand-icon" aria-hidden="true">
            <GoogleCalendarLogo size={26} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>
              {t(loading ? 'calset.checking' : failed ? 'calset.failed' : !hasServices ? 'calset.noServices' : connected ? 'calset.connected' : 'calset.notConnected')}
            </h3>
            <p>
              {t(loading ? 'calset.checkingBody' : failed ? 'calset.failedBody' : !hasServices ? 'calset.noServicesBody' : connected ? 'calset.connectedBody' : 'calset.emptyBody')}
            </p>
          </div>
          {connected && !loading ? (
            <span className="dash-connect-status is-ok">
              <IconCheck size={13} aria-hidden="true" />
              {t('calset.statusOk')}
            </span>
          ) : null}
        </div>

        {lines.length ? (
          <ul className="dash-calset-list">
            {lines.map((line) => (
              <li key={line.key}><span className="dash-calset-logo" aria-hidden="true">{line.logo}</span>{line.text}</li>
            ))}
          </ul>
        ) : (
          /* Sin nada conectado: los tres que se pueden, por su logo. Es lo
             primero que busca quien llega: «¿está el mío?». */
          <ul className="dash-calset-providers" aria-label={t('calset.providers')}>
            <li><span className="dash-calset-logo"><GoogleCalendarLogo size={20} /></span>{t('calset.google.name')}</li>
            <li><span className="dash-calset-logo is-outlook"><IconBrandWindows size={18} /></span>{t('calset.outlook')}</li>
            <li><span className="dash-calset-logo is-apple"><IconBrandApple size={19} /></span>{t('calset.apple')}</li>
          </ul>
        )}

        {!loading && !failed ? (
          <div className="dash-settings-card-foot">
            {hasServices ? (
              <button type="button" className="dash-primary-btn" onClick={() => setOpen(true)}>
                <IconCalendarShare size={14} aria-hidden="true" />
                {t(connected ? 'calset.manage' : 'calset.connect')}
              </button>
            ) : (
              <button type="button" className="dash-primary-btn" onClick={onOpenBookings}>
                <IconPlus size={14} aria-hidden="true" />
                {t('calset.createService')}
              </button>
            )}
          </div>
        ) : failed ? (
          <div className="dash-settings-card-foot">
            <button type="button" className="dash-secondary-btn" onClick={() => { setLoading(true); void load() }}>{t('calset.retry')}</button>
          </div>
        ) : null}
      </section>

      {/* Qué hace, dicho en la misma pantalla: las dos direcciones. */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconListCheck size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('calset.whatTitle')}</h3>
          </div>
        </div>
        <ol className="dash-connect-steps">
          <li><span className="dash-connect-step-num">1</span>{t('calset.what1')}</li>
          <li><span className="dash-connect-step-num">2</span>{t('calset.what2')}</li>
          <li><span className="dash-connect-step-num">3</span>{t('calset.what3')}</li>
        </ol>
      </section>

      {open ? (
        <CalendarSyncDialog
          projectId={projectId}
          token={token}
          resources={resources}
          onClose={() => { setOpen(false); void load() }}
        />
      ) : null}
    </ProjectSettingsLayout>
  )
}
